import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { fetchAdminConfig, updateConfig, deleteConfig, seedConfig } from '../../api/api';

const BACKEND = 'https://backend-mtaani.onrender.com';

const CATEGORIES = ['general', 'app', 'payments', 'notifications', 'features', 'ui'];
const CAT_LABELS = { general: 'General', app: 'App', payments: 'Payments', notifications: 'Notifications', features: 'Features', ui: 'UI' };
const CAT_ICONS = { general: '⚙️', app: '📱', payments: '💳', notifications: '🔔', features: '✨', ui: '🎨' };

const TYPE_BADGE = {
  boolean: { bg: '#dcfce7', color: '#166534' },
  number:  { bg: '#fef9c3', color: '#854d0e' },
  string:  { bg: '#f3f4f6', color: '#374151' },
  color:   { bg: '#ede9fe', color: '#5b21b6' },
  json:    { bg: '#dbeafe', color: '#1e40af' },
  image:   { bg: '#fce7f3', color: '#9d174d' },
};

function Badge({ type }) {
  const s = TYPE_BADGE[type] || TYPE_BADGE.string;
  return (
    <span style={{ background: s.bg, color: s.color, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, letterSpacing: 0.3 }}>
      {type}
    </span>
  );
}

function PublicBadge() {
  return (
    <span style={{ background: '#dbeafe', color: '#1d4ed8', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>
      public
    </span>
  );
}

function ValueDisplay({ item }) {
  if (item.type === 'boolean') return null;
  if (item.type === 'color') return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 16, height: 16, borderRadius: 4, background: item.value, border: '1px solid #e5e7eb' }} />
      <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#374151' }}>{item.value}</span>
    </div>
  );
  if (item.type === 'json') return <span style={{ fontSize: 12, color: '#6b7280', fontFamily: 'monospace' }}>{'{...}'}</span>;
  return (
    <span style={{ fontSize: 13, fontFamily: 'monospace', color: '#374151', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
      {String(item.value ?? '')}
    </span>
  );
}

function ConfigCard({ item, onSave, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [editVal, setEditVal] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const inputRef = useRef(null);

  const openEdit = () => {
    setEditVal(item.type === 'json' ? JSON.stringify(item.value, null, 2) : String(item.value ?? ''));
    setExpanded(true);
    setTimeout(() => inputRef.current?.focus(), 80);
  };

  const handleToggle = async () => {
    setSaving(true);
    try {
      const newVal = !item.value;
      await updateConfig(item.key, { value: newVal });
      onSave(item.key, newVal);
    } finally { setSaving(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let val = editVal;
      if (item.type === 'number') val = Number(editVal);
      if (item.type === 'json') { try { val = JSON.parse(editVal); } catch { alert('Invalid JSON'); setSaving(false); return; } }
      await updateConfig(item.key, { value: val });
      onSave(item.key, val);
      setExpanded(false);
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteConfig(item.key);
      onDelete(item.key);
    } finally { setDeleting(false); setConfirmDel(false); }
  };

  return (
    <div style={{
      background: '#fff',
      border: expanded ? '1.5px solid #6366f1' : '1px solid #e5e7eb',
      borderRadius: 14,
      overflow: 'hidden',
      transition: 'border-color 0.15s, box-shadow 0.15s',
      boxShadow: expanded ? '0 0 0 3px #eef2ff' : '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      {/* Card header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 2 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{item.label || item.key}</span>
            <Badge type={item.type || 'string'} />
            {item.isPublic && <PublicBadge />}
          </div>
          <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#9ca3af' }}>{item.key}</span>
          {item.description && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{item.description}</div>}
        </div>

        {/* Value preview + controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {item.type === 'boolean' ? (
            <button
              onClick={handleToggle}
              disabled={saving}
              style={{
                width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                background: item.value ? '#6366f1' : '#d1d5db',
                position: 'relative', transition: 'background 0.2s',
              }}
            >
              <div style={{
                position: 'absolute', top: 3, left: item.value ? 23 : 3,
                width: 18, height: 18, borderRadius: 9, background: '#fff',
                transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }} />
            </button>
          ) : (
            <div style={{ maxWidth: 180 }}><ValueDisplay item={item} /></div>
          )}

          {item.type !== 'boolean' && (
            <button
              onClick={expanded ? () => setExpanded(false) : openEdit}
              style={{ background: expanded ? '#f3f4f6' : '#eef2ff', border: 'none', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: expanded ? '#6b7280' : '#6366f1' }}
            >
              {expanded ? 'Cancel' : 'Edit'}
            </button>
          )}

          {!confirmDel ? (
            <button
              onClick={() => setConfirmDel(true)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', fontSize: 16, padding: '4px 6px', borderRadius: 6, lineHeight: 1 }}
              title="Delete"
            >×</button>
          ) : (
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={handleDelete} disabled={deleting} style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                {deleting ? '…' : 'Delete'}
              </button>
              <button onClick={() => setConfirmDel(false)} style={{ background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}>
                No
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Inline editor */}
      {expanded && (
        <div style={{ borderTop: '1px solid #eef2ff', padding: '14px 16px', background: '#fafafa' }}>
          {item.type === 'color' ? (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <input type="color" value={editVal} onChange={e => setEditVal(e.target.value)}
                style={{ width: 48, height: 40, border: 'none', borderRadius: 8, cursor: 'pointer', background: 'none' }} />
              <input
                ref={inputRef}
                value={editVal}
                onChange={e => setEditVal(e.target.value)}
                style={{ flex: 1, border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '9px 14px', fontSize: 14, fontFamily: 'monospace', outline: 'none' }}
              />
            </div>
          ) : item.type === 'json' ? (
            <textarea
              ref={inputRef}
              value={editVal}
              onChange={e => setEditVal(e.target.value)}
              rows={6}
              style={{ width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '10px 14px', fontSize: 13, fontFamily: 'monospace', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
            />
          ) : (
            <input
              ref={inputRef}
              type={item.type === 'number' ? 'number' : 'text'}
              value={editVal}
              onChange={e => setEditVal(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSave()}
              style={{ width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '9px 14px', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
            />
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
            <button onClick={() => setExpanded(false)} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#6b7280' }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#fff', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AddConfigForm({ onAdd, onCancel }) {
  const EMPTY = { key: '', value: '', type: 'string', label: '', description: '', category: 'general', isPublic: false };
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.key.trim()) return alert('Key is required');
    setSaving(true);
    try {
      let val = form.value;
      if (form.type === 'number') val = Number(form.value);
      if (form.type === 'boolean') val = form.value === 'true';
      if (form.type === 'json') { try { val = JSON.parse(form.value); } catch { alert('Invalid JSON'); setSaving(false); return; } }
      await updateConfig(form.key, { value: val, type: form.type, label: form.label, description: form.description, category: form.category, isPublic: form.isPublic });
      onAdd();
    } finally { setSaving(false); }
  };

  const inp = (placeholder, key, type = 'text') => (
    <input
      type={type} placeholder={placeholder} value={form[key]}
      onChange={e => set(key, e.target.value)}
      style={{ border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '9px 14px', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' }}
    />
  );

  return (
    <div style={{ background: '#fff', border: '1.5px solid #6366f1', borderRadius: 16, padding: 20, boxShadow: '0 0 0 3px #eef2ff', marginBottom: 16 }}>
      <div style={{ fontWeight: 800, fontSize: 15, color: '#111827', marginBottom: 16 }}>New Configuration</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        {inp('Key * (e.g. mpesa.paybill_number)', 'key')}
        {inp('Label (display name)', 'label')}
      </div>
      {inp('Description (optional)', 'description')}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, margin: '12px 0' }}>
        <select value={form.type} onChange={e => set('type', e.target.value)}
          style={{ border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '9px 14px', fontSize: 14, outline: 'none', background: '#fff' }}>
          {['string', 'number', 'boolean', 'color', 'json', 'image'].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={form.category} onChange={e => set('category', e.target.value)}
          style={{ border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '9px 14px', fontSize: 14, outline: 'none', background: '#fff' }}>
          {CATEGORIES.map(c => <option key={c} value={c}>{CAT_LABELS[c]}</option>)}
        </select>
        <input
          placeholder={form.type === 'boolean' ? 'true / false' : form.type === 'json' ? '{"key":"val"}' : 'Value *'}
          value={form.value}
          onChange={e => set('value', e.target.value)}
          style={{ border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '9px 14px', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#6b7280', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.isPublic} onChange={e => set('isPublic', e.target.checked)} style={{ width: 16, height: 16 }} />
          Public (visible to app without auth)
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onCancel} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#6b7280' }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving} style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none', borderRadius: 8, padding: '8px 22px', fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#fff', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Adding…' : 'Add Config'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AppConfiguration() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('general');
  const [showAdd, setShowAdd] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [toast, setToast] = useState(null);
  const [liveConnected, setLiveConnected] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
    const socket = io(BACKEND, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      setLiveConnected(true);
      const userId = localStorage.getItem('userId');
      if (userId) socket.emit('auth', userId);
    });
    socket.on('disconnect', () => setLiveConnected(false));

    socket.on('config:updated', ({ key, value }) => {
      setConfigs(cs => cs.map(c => c.key === key ? { ...c, value } : c));
    });

    return () => socket.disconnect();
  }, []);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetchAdminConfig();
      const raw = res.data?.configs ?? res.data ?? {};
      // backend returns grouped object — flatten all categories into one array
      const flat = Array.isArray(raw)
        ? raw
        : Object.values(raw).flat();
      setConfigs(flat);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await seedConfig();
      await load();
      showToast('Default config seeded');
    } catch { showToast('Seeding failed', false); }
    finally { setSeeding(false); }
  };

  const handleSave = (key, value) => {
    setConfigs(cs => cs.map(c => c.key === key ? { ...c, value } : c));
    showToast('Saved');
  };

  const handleDelete = (key) => {
    setConfigs(cs => cs.filter(c => c.key !== key));
    showToast('Deleted');
  };

  const handleAdd = async () => {
    setShowAdd(false);
    await load();
    showToast('Config added');
  };

  const grouped = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = configs.filter(c => (c.category || 'general').toLowerCase() === cat.toLowerCase());
    return acc;
  }, {});

  const activeItems = grouped[activeTab] || [];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f9fafb', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* Sidebar */}
      <div style={{ width: 220, background: '#fff', borderRight: '1px solid #e5e7eb', padding: '24px 12px', flexShrink: 0, position: 'sticky', top: 0, height: '100vh', overflowY: 'auto' }}>
        <div style={{ fontWeight: 800, fontSize: 13, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16, paddingLeft: 12 }}>
          Categories
        </div>
        {CATEGORIES.map(cat => {
          const count = grouped[cat]?.length ?? 0;
          const active = activeTab === cat;
          return (
            <button
              key={cat}
              onClick={() => setActiveTab(cat)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                borderRadius: 10, border: 'none', cursor: 'pointer', textAlign: 'left', marginBottom: 2,
                background: active ? '#eef2ff' : 'transparent',
                color: active ? '#6366f1' : '#374151',
                fontWeight: active ? 700 : 500, fontSize: 14,
                transition: 'background 0.15s',
              }}
            >
              <span style={{ fontSize: 16 }}>{CAT_ICONS[cat]}</span>
              <span style={{ flex: 1 }}>{CAT_LABELS[cat]}</span>
              <span style={{
                background: active ? '#6366f1' : '#f3f4f6',
                color: active ? '#fff' : '#6b7280',
                fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '1px 7px',
              }}>{count}</span>
            </button>
          );
        })}

        <div style={{ marginTop: 24, borderTop: '1px solid #f3f4f6', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            onClick={handleSeed}
            disabled={seeding}
            style={{ background: '#f3f4f6', border: 'none', borderRadius: 10, padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#374151', opacity: seeding ? 0.6 : 1 }}
          >
            {seeding ? '⏳ Seeding…' : '🌱 Seed Defaults'}
          </button>
          <button
            onClick={() => { setShowAdd(true); }}
            style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none', borderRadius: 10, padding: '9px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#fff' }}
          >
            + Add Config
          </button>
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1, padding: 32, maxWidth: 900, overflowY: 'auto' }}>
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
            <span style={{ fontSize: 24 }}>{CAT_ICONS[activeTab]}</span>
            <h1 style={{ margin: 0, fontWeight: 800, fontSize: 22, color: '#111827' }}>{CAT_LABELS[activeTab]} Settings</h1>
          </div>
          <p style={{ margin: 0, fontSize: 14, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 8 }}>
            {configs.length} total entries · Changes take effect immediately in the app
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: liveConnected ? '#dcfce7' : '#f3f4f6', color: liveConnected ? '#166534' : '#6b7280', fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 20 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: liveConnected ? '#22c55e' : '#d1d5db', display: 'inline-block' }} />
              {liveConnected ? 'Live' : 'Connecting…'}
            </span>
          </p>
        </div>

        {showAdd && (
          <AddConfigForm onAdd={handleAdd} onCancel={() => setShowAdd(false)} />
        )}

        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <div style={{ width: 32, height: 32, border: '3px solid #e5e7eb', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          </div>
        )}

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '12px 16px', color: '#dc2626', fontSize: 14, marginBottom: 16 }}>
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {activeItems.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 24px', color: '#9ca3af' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>{CAT_ICONS[activeTab]}</div>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6, color: '#374151' }}>No entries yet</div>
                <div style={{ fontSize: 14 }}>Click "Seed Defaults" to add default config, or "+ Add Config" to create one.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {activeItems.map(item => (
                  <ConfigCard key={item.key} item={item} onSave={handleSave} onDelete={handleDelete} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          background: toast.ok ? '#111827' : '#dc2626',
          color: '#fff', padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          animation: 'fadeIn 0.2s ease',
        }}>
          {toast.ok ? '✓' : '✕'} {toast.msg}
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
